import axiosInstance from "./AxiosConfig";

const TOKEN_KEY = "token";
const USER_KEY = "authenticatedUserObject";
const AUTH_EVENT = "pyj:auth-changed";

// Storage can throw (Safari private mode, blocked cookies): never let that break the app.
const safeGet = (storage, key) => {
    try { return storage.getItem(key); } catch { return null; }
};
const safeSet = (storage, key, value) => {
    try { storage.setItem(key, value); } catch { /* ignore */ }
};
const safeRemove = (storage, key) => {
    try { storage.removeItem(key); } catch { /* ignore */ }
};

export const registerAPICall = (registerObj) => axiosInstance.post('api/auth/register', registerObj)

export const loginAPICall = (usernameOrEmail, password) => axiosInstance.post('api/auth/login', { usernameOrEmail: (usernameOrEmail || '').trim(), password })

export const forgotPasswordAPICall = (email, lang) => axiosInstance.post('api/auth/forgot-password', { email: (email || '').trim(), lang })

export const resetPasswordAPICall = (token, password) => axiosInstance.post('api/auth/reset-password', { token, password })

export const updateUser = (userId, user) => axiosInstance.put('api/auth/' + userId, user);

export const deleteUser = (userId) => axiosInstance.delete('api/auth/' + userId);

const cleanBearer = (token) => {
    let cleanToken = String(token || '').trim();
    while (cleanToken.startsWith("Bearer ")) {
        cleanToken = cleanToken.substring(7).trim();
    }
    return cleanToken;
};

export const storeToken = (token) => {
    const cleanToken = cleanBearer(token);
    if (cleanToken) {
        safeSet(localStorage, TOKEN_KEY, cleanToken);
    }
}

export const getToken = () => safeGet(localStorage, TOKEN_KEY)

/** Expiry (ms) read from the JWT payload, or null when it cannot be decoded. */
const getTokenExpiry = (token) => {
    try {
        const payload = token.split('.')[1];
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return json.exp ? json.exp * 1000 : null;
    } catch {
        return null;
    }
};

export const isTokenExpired = (token = getToken()) => {
    if (!token) return true;
    const exp = getTokenExpiry(token);
    return exp !== null && exp <= Date.now();
};

const readUser = () => {
    // The user used to be kept in sessionStorage only: closing the tab (or iOS discarding it)
    // logged people out while the token stayed around. Migrate it to localStorage.
    let raw = safeGet(localStorage, USER_KEY);
    if (!raw) {
        raw = safeGet(sessionStorage, USER_KEY);
        if (raw) {
            safeSet(localStorage, USER_KEY, raw);
            safeRemove(sessionStorage, USER_KEY);
        }
    }
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const notifyAuthChange = () => {
    try { window.dispatchEvent(new Event(AUTH_EVENT)); } catch { /* ignore */ }
};

/** Subscribe to login/logout (also from other tabs). Returns the unsubscribe function. */
export const onAuthChange = (callback) => {
    const onStorage = (e) => {
        if (!e.key || e.key === TOKEN_KEY || e.key === USER_KEY) callback();
    };
    window.addEventListener(AUTH_EVENT, callback);
    window.addEventListener('storage', onStorage);
    return () => {
        window.removeEventListener(AUTH_EVENT, callback);
        window.removeEventListener('storage', onStorage);
    };
};

export const saveLoggedInUser = (username, role, userId, email, name, surname, userNotifications, isPublic) => {
    safeSet(localStorage, USER_KEY, JSON.stringify({ userId, username, role, email, name, surname, userNotifications: userNotifications || [], isPublic }))
}

/** Merge fields into the stored user (e.g. after editing the profile). */
export const updateStoredUser = (fields) => {
    const current = readUser() || {};
    safeSet(localStorage, USER_KEY, JSON.stringify({ ...current, ...fields }));
    notifyAuthChange();
};

export const removeSessionStorage = () => {
    safeRemove(sessionStorage, USER_KEY);
    safeRemove(localStorage, USER_KEY);
}

export const isUserLoggedIn = () => {
    const user = readUser();
    const token = getToken();
    return Boolean(user && user.userId && token && !isTokenExpired(token));
}

export const getLoggedInUser = () => readUser()?.username ?? null

export const getLoggedInUserId = () => (isUserLoggedIn() ? readUser()?.userId ?? null : null)

/** Kept for backwards compatibility: returns the JSON string of the stored user. */
export const getLoggedInUserObject = () => {
    const user = readUser();
    return user ? JSON.stringify(user) : null;
}

export const getCurrentUser = () => (isUserLoggedIn() ? readUser() : null)

export const logout = () => {
    // Only drop auth data: clearing everything also wiped the chosen language.
    safeRemove(localStorage, TOKEN_KEY);
    safeRemove(localStorage, USER_KEY);
    safeRemove(sessionStorage, USER_KEY);
    notifyAuthChange();
}

export const isAdminUser = () => readUser()?.role === "ROLE_ADMIN"
